import {Form, FormField, FormSubmitHandler, Separator, BreadcrumbType} from 'platform/components';
import {Grid, Heading, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetBranchListQuery,
  useGetGeneralSettingQuery,
  usePatchGeneralSettingMutation,
  usePostGeneralSettingMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds, settingsRoutes} from '@dms/routes';
import {
  SettingsFooter,
  SettingsSection,
  SettingsTemplate,
  useSeriesOptions,
} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {tiresInventoryFormSchema, TiresInventoryFormType} from './types/tiresInventoryFormSchema';
import {getDefaultValuesFromGeneralSettings} from './utils/getDefaultValuesFromGeneralSettings';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('general.labels.general'),
    href: settingsRoutes.tiresInventory,
  },
];

export function TiresInventoryDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetGeneralSettingQuery(
    {generalSettingId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {data: branches} = useGetBranchListQuery();
  const {groupedSeries, isSeriesLoading} = useSeriesOptions([
    'pneu-hotel/tire-movement',
    'pneu-hotel/tire-order',
    'pneu-hotel/tire-set',
  ]);

  const getBranchOptions = () =>
    branches?.branchListItems.map((branch) => ({label: branch.marketingName, value: branch.id})) ??
    [];

  const [postGeneralSetting] = usePostGeneralSettingMutation();
  const [patchGeneralSetting] = usePatchGeneralSettingMutation();

  const handleSubmit: FormSubmitHandler<TiresInventoryFormType> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      ...data,
      hasAutomaticStoreUntilSettings: data.hasAutomaticStoreUntilSettings ?? false,
    };
    const request = isNil(id)
      ? postGeneralSetting({
          body,
        })
      : patchGeneralSetting({body, generalSettingId: id});

    await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tiresInventory);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{title: i18n.t('general.labels.general'), breadcrumbs}}
      description={i18n.t('entity.tiresInventory.labels.settingsDescription')}
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.tiresInventory('template')}
    >
      <Form<TiresInventoryFormType>
        defaultValues={getDefaultValuesFromGeneralSettings(data)}
        experimentalZodSchema={tiresInventoryFormSchema}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control) => (
          <>
            <VStack spacing={6}>
              <FormField
                control={control}
                type="text"
                name="name"
                label={i18n.t('general.labels.name')}
                isRequired
                data-testid={testIds.settings.tiresInventory('name')}
              />
              <FormField
                control={control}
                type="multiChoice"
                name="branchIds"
                label={i18n.t('entity.branch.labels.branch')}
                options={getBranchOptions()}
                isRequired
                data-testid={testIds.settings.tiresInventory('branch')}
              />
            </VStack>
            <Separator />
            <SettingsSection>
              <VStack spacing={6}>
                <Heading size={4}>{i18n.t('entity.documentSeries.labels.documentSeries')}</Heading>
                <Grid columns={3} spacing={4}>
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    type="choice"
                    name="documentSettings.receiptNote.docSeriesId"
                    label={i18n.t('entity.tiresInventory.labels.receiptNote')}
                    isLoading={isSeriesLoading}
                    options={groupedSeries['pneu-hotel/tire-movement'] ?? []}
                    data-testid={testIds.settings.tiresInventory('receiptNote')}
                  />
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    type="choice"
                    name="documentSettings.issueNote.docSeriesId"
                    label={i18n.t('entity.tiresInventory.labels.issueNote')}
                    isLoading={isSeriesLoading}
                    options={groupedSeries['pneu-hotel/tire-movement'] ?? []}
                    data-testid={testIds.settings.tiresInventory('issueNote')}
                  />
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    type="choice"
                    name="documentSettings.archiveNote.docSeriesId"
                    label={i18n.t('entity.tiresInventory.labels.archiveNote')}
                    isLoading={isSeriesLoading}
                    options={groupedSeries['pneu-hotel/tire-movement'] ?? []}
                    data-testid={testIds.settings.tiresInventory('archiveNote')}
                  />
                </Grid>
                <Grid columns={3} spacing={4}>
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    type="choice"
                    name="documentSettings.tireOrder.docSeriesId"
                    label={i18n.t('page.tiresInventory.labels.tireOrder')}
                    isLoading={isSeriesLoading}
                    options={groupedSeries['pneu-hotel/tire-order'] ?? []}
                    data-testid={testIds.settings.tiresInventory('tireOrder')}
                  />
                  <FormField
                    isRequired
                    isNotClearable
                    control={control}
                    type="choice"
                    name="documentSettings.tireSet.docSeriesId"
                    label={i18n.t('entity.tireSet.labels.tireSet')}
                    isLoading={isSeriesLoading}
                    options={groupedSeries['pneu-hotel/tire-set'] ?? []}
                    data-testid={testIds.settings.tiresInventory('tireSet')}
                  />
                </Grid>
                <FormField
                  control={control}
                  type="checkbox"
                  name="hasAutomaticStoreUntilSettings"
                  label={i18n.t('page.tiresInventory.labels.hasAutomaticStoreUntilSettings')}
                  data-testid={testIds.settings.tiresInventory('hasAutomaticStoreUntilSettings')}
                />
                <Grid columns={3} spacing={4}>
                  <FormField
                    control={control}
                    type="dayMonth"
                    name="storeUntilSettings.0.receivedAt"
                    label={i18n.t('page.tiresInventory.labels.receivedAt')}
                    data-testid={testIds.settings.tiresInventory('storeUntilSettings-0-receivedAt')}
                  />
                  <FormField
                    control={control}
                    type="dayMonth"
                    name="storeUntilSettings.0.receivedTo"
                    label={i18n.t('page.tiresInventory.labels.receivedTo')}
                    data-testid={testIds.settings.tiresInventory('storeUntilSettings-0-receivedTo')}
                  />
                  <FormField
                    control={control}
                    type="dayMonth"
                    name="storeUntilSettings.0.preFillDate"
                    label={i18n.t('page.tiresInventory.labels.preFillDate')}
                    data-testid={testIds.settings.tiresInventory(
                      'storeUntilSettings-0-preFillDate'
                    )}
                  />
                </Grid>
                <Grid columns={3} spacing={4}>
                  <FormField
                    control={control}
                    type="dayMonth"
                    name="storeUntilSettings.1.receivedAt"
                    label={i18n.t('page.tiresInventory.labels.receivedAt')}
                    data-testid={testIds.settings.tiresInventory('storeUntilSettings-1-receivedAt')}
                  />
                  <FormField
                    control={control}
                    type="dayMonth"
                    name="storeUntilSettings.1.receivedTo"
                    label={i18n.t('page.tiresInventory.labels.receivedTo')}
                    data-testid={testIds.settings.tiresInventory('storeUntilSettings-1-receivedTo')}
                  />
                  <FormField
                    control={control}
                    type="dayMonth"
                    name="storeUntilSettings.1.preFillDate"
                    label={i18n.t('page.tiresInventory.labels.preFillDate')}
                    data-testid={testIds.settings.tiresInventory(
                      'storeUntilSettings-1-preFillDate'
                    )}
                  />
                </Grid>
                <SettingsFooter
                  actions={[
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.save'),
                    },
                  ]}
                />
              </VStack>
            </SettingsSection>
          </>
        )}
      </Form>
    </SettingsTemplate>
  );
}
